<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-start tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Disputed Charges</h1>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" ref="searchInput" class="tw-w-full" placeholder="Search for names or dispute reasons" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <div class="tw-flex tw-justify-end tw-w-full">
        <base-button type="flat" class="tw-mt-3" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
      </div>
      <base-table :loading="isLoading" :fields="fields" :items="disputes" class="tw-mt-2">
        <template #member="{ item: dispute }">
          <div class="tw-flex">
            <base-button v-if="dispute.userId !== null" type="text" size="md" @click="gotoMember(dispute.userId)">
              <span class="tw-whitespace-nowrap tw-text-left">{{ dispute.userName }}</span>
            </base-button>
            <div class="tw-flex" v-else>
              <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ dispute.userName }}</div>
              <base-badge class="tw-ml-3 tw-my-auto tw-flex-shrink-0" size="sm" color="yellow">No match</base-badge>
            </div>
          </div>
        </template>
        <template #submitted="{ item: dispute }">
          <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(dispute.createdDate) }}</div>
          <div class="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ formatTime(dispute.createdDate) }}</div>
        </template>
        <template #amount="{ item: dispute }">
          <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap"><money-format :value="dispute.amount" :locale="locale" subunits-value /></div>
        </template>
        <template #reason="{ item: dispute }">
          <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ dispute.reason }}</div>
        </template>
        <template #status="{ item: dispute }">
          <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ findStatusOption(dispute.status) }}</div>
        </template>
        <template #actions="{ item: dispute }">
          <div class="tw-flex tw-items-center tw-space-x-2">
            <base-button type="flat" class="tw-whitespace-nowrap" @click="view(dispute)">
              <template v-slot:icon>
                <tool-icon /> </template
              >Manage in Stripe
            </base-button>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
              <base-skeleton-loader class="tw-h-3 tw-w-12 tw-rounded-full tw-ml-3" />
            </div>
            <base-skeleton-loader class="tw-h-3 tw-w-32 tw-mt-2" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4" v-if="organization.collectDobSetting">
            <base-skeleton-loader class="tw-h-4 tw-w-20" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-4 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-56" />
          </td>
          <td v-if="isLocationFiltered === false" class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-space-x-6 tw-justify-center">
              <base-skeleton-loader class="tw-h-6 tw-w-20" />
            </div>
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <dispute-filter ref="filterSlideOver" :business-id="businessId" @filter-updated="applyFilter" />
  </div>
</template>

<script>
import DisputeFilter from "@/views/business/dispute/components/DisputeFilter";
import { debounce } from "debounce";
import { mapGetters } from "vuex";
import moment from "moment";
import { SearchIcon, FilterIcon, ToolIcon } from "@/assets/svgs";
import { isEqual } from "lodash";
import MoneyFormat from "vue-money-format";

const TAKE_KEY = "predictile_business_disputes_take";

export default {
  name: "BusinessDisputes",
  components: {
    MoneyFormat,
    DisputeFilter,
    SearchIcon,
    FilterIcon,
    ToolIcon,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      disputes: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      query: null,
      isLoading: false,
      filters: {
        statusFilter: [],
      },
      statusOptions: [
        { label: "Lost", value: "lost" },
        { label: "Needs response", value: "needs_response" },
        { label: "Under review", value: "under_review" },
        { label: "Warning - Closed", value: "warning_closed" },
        { label: "Warning - Needs response", value: "warning_needs_response" },
        { label: "Warning - Under review", value: "warning_under_review" },
        { label: "Won", value: "won" },
      ],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered"]),
    locale() {
      return window.navigator.language;
    },
    fields() {
      let columns = [];

      columns.push({ title: this.organization.customerLabelSingular, key: "member" });
      columns.push({ title: "Submitted", key: "submitted" });
      columns.push({ title: "Disputed Amount", key: "amount" });
      columns.push({ title: "Reason", key: "reason" });
      columns.push({ title: "Status", key: "status" });
      columns.push({ title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" });

      return columns;
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    this.$refs.searchInput.focus();
  },
  methods: {
    findStatusOption(value) {
      return this.statusOptions.find((option) => option.value === value).label;
    },
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      if (!isEqual(this.filters, filters)) {
        this.filters = filters;
        this.loadPage();
      } else {
        this.filters = filters;
      }
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    view(dispute) {
      window.open(`https://dashboard.stripe.com/disputes/${dispute.disputeId}`, "_blank");
    },
    gotoMember(memberId) {
      window.open(`/businesses/${this.businessId}/members/${memberId}/history`, "_blank");
    },
    loadPage() {
      this.isLoading = true;

      let filters = { statusFilter: [] };
      if (this.filters !== undefined) filters = this.filters;

      let url = `/businesses/${this.businessId}/disputes/search?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .post(url, { statusFilter: filters.statusFilter })
        .then((res) => {
          this.disputes = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load disputes",
            message: "We had a problem retrieving the list of disputes. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY");
    },
    formatTime(date) {
      return new moment.utc(date).local().format("h:mm A");
    },
  },
};
</script>
